import queryString from "query-string";
import { store } from "../store";
import JSZip from "jszip";
import axios from "axios";

export function encodeParams(
  params: { [key: string]: string | number | boolean } = {}
): string {
  try {
    // if property is falsy, delete it from object
    Object.keys(params).forEach((key) => params[key] || delete params[key]);

    return queryString.stringify(params);
  } catch (e) {
    return "";
  }
}

/**
 * Extracts a filename from the Content-Disposition HTTP header.
 *
 * Supports both the UTF-8 encoded `filename*` and the standard `filename` fallback.
 *
 * @param contentDisposition - The value of the Content-Disposition header.
 * @returns The extracted filename, or null if not found.
 */
export function getFilenameFromContentDisposition(
  contentDisposition: string | null
): string | null {
  if (!contentDisposition) return null;

  try {
    // Check for UTF-8 encoded filename*: filename*=UTF-8''encoded_name
    const utf8Match = contentDisposition.match(
      /filename\*=(?:UTF-8'')?([^;]+)/
    );
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    // Fallback to simple filename="name"
    const fallbackMatch = contentDisposition.match(/filename="([^"]+)"/);
    if (fallbackMatch && fallbackMatch[1]) {
      return fallbackMatch[1];
    }

    // If neither pattern matches, return null.
    return null;
  } catch (error) {
    console.error("Error parsing Content-Disposition:", error);
    return null;
  }
}

export function getUserId() {
  return store.getState().user.id;
}

/**
 * Saves a Blob (binary data) as a downloadable file on the client machine.
 *
 * @param blobData - The Blob object containing binary data (e.g., ZIP file).
 * @param filename - The desired filename for the downloaded file (default: "download.zip").
 */
export function saveBlobToFile(
  blobData: Blob,
  filename = "download.zip"
): void {
  try {
    // Create a temporary object URL pointing to the blob's binary data.
    // This lets the browser treat it like a file reference.
    const url: string = window.URL.createObjectURL(blobData);

    // Create an <a> element that we'll programmatically click to start the download.
    const linkElement: HTMLAnchorElement = document.createElement("a");

    // Add the link to the document body to ensure compatibility in all browsers.
    document.body.appendChild(linkElement);

    // Hide the link from view (it's only used for the click action).
    linkElement.style.display = "none";

    // Set the link's href to the blob URL and the download attribute to the filename.
    linkElement.href = url;
    linkElement.download = filename;

    // Trigger a click on the link to prompt the browser to download the file.
    linkElement.click();

    // Release the blob URL to free up memory.
    window.URL.revokeObjectURL(url);

    // Clean up the DOM by removing the temporary link element.
    document.body.removeChild(linkElement);
  } catch (error) {
    console.error("Failed to save blob data:", error);
  }
}

export function normalizeError(e: unknown): Error {
  if (e instanceof Error && !axios.isAxiosError(e)) return e;

  if (axios.isAxiosError(e)) {
    const data: any = e.response?.data;

    // plain string from backend
    if (typeof data === "string" && data.trim().length > 0) {
      return new Error(data);
    }

    // common shapes
    if (data?.message) return new Error(String(data.message));
    if (data?.error) return new Error(String(data.error));

    // ASP.NET Core ProblemDetails-ish
    if (data?.title && data?.detail)
      return new Error(`${data.title}: ${data.detail}`);
    if (data?.title) return new Error(String(data.title));
    if (data?.detail) return new Error(String(data.detail));

    // ASP.NET Core model validation: { errors: { Field: ["msg1","msg2"] } }
    if (data?.errors && typeof data.errors === "object") {
      const flat = Object.values(data.errors)
        .flat()
        .map((x) => String(x))
        .filter(Boolean);

      if (flat.length) return new Error(flat.join("\n"));
    }

    // fallback (still better than nothing)
    return new Error(e.message || "Request failed.");
  }

  // non-Axios / non-Error throw
  return new Error(typeof e === "string" ? e : "An unexpected error occurred.");
}

export function saveJsonToFile(jsonData: string, filename = "data.json") {
  try {
    const dataStr = JSON.stringify(jsonData);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const linkElement = document.createElement("a");
    document.body.appendChild(linkElement); // Adding to DOM to ensure compatibility in some browsers
    linkElement.style.display = "none";
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", filename);
    linkElement.click();
    document.body.removeChild(linkElement); // Clean up
  } catch (error) {
    console.error("Failed to export JSON data: ", error);
  }
}

export function saveJsonToZipFile(jsonData, filename = "data.json") {
  const zip = new JSZip();
  const jsonStr = JSON.stringify(jsonData);
  zip.file(filename, jsonStr); // Add the JSON file to the zip

  zip
    .generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 9,
      },
      mimeType: "application/zip",
    })
    .then((content) => {
      // Generate the zip file and trigger the download
      const url = window.URL.createObjectURL(content);
      const linkElement = document.createElement("a");
      document.body.appendChild(linkElement); // Adding to DOM to ensure compatibility in some browsers
      linkElement.style.display = "none";
      linkElement.href = url;
      linkElement.download = filename.replace(".json", ".zip"); // Change the extension to .zip
      linkElement.click();
      document.body.removeChild(linkElement); // Clean up
      window.URL.revokeObjectURL(url); // Free up memory by releasing the blob URL
    })
    .catch((error) => {
      console.error("Failed to zip JSON data: ", error);
    });
}

export function exportToTSVFile(tsvData: string, filename = "data.tsv") {
  //const dataStr = JSON.stringify(tsvData);
  const dataUri = "data:text/tsv;charset=utf-8," + encodeURIComponent(tsvData);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}
